import { useCallback, useEffect, useRef, useState } from "react";

const SRC = "/audio/relaxing-piano.mp3";
const VOLUME = 0.25;
const FADE = 2.5;

// Web Audio gain so fades also work on iOS, where media.volume is read-only.
export const useAmbientSound = () => {
  const audioRef = useRef(null);
  const graphRef = useRef(null);
  const userOffRef = useRef(false);
  const [playing, setPlaying] = useState(false);

  const ensureGraph = () => {
    if (graphRef.current) return graphRef.current;
    const audio = new Audio(SRC);
    audio.preload = "none";
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const gain = ctx.createGain();
    gain.gain.value = 0;
    ctx.createMediaElementSource(audio).connect(gain).connect(ctx.destination);
    audio.addEventListener("timeupdate", () => {
      if (audio.duration && audio.duration - audio.currentTime < FADE + 0.2) ramp(0);
    });
    audio.addEventListener("ended", () => { audio.currentTime = 0; setPlaying(false); });
    audioRef.current = audio;
    graphRef.current = { ctx, gain };
    return graphRef.current;
  };

  const ramp = (to) => {
    const { ctx, gain } = graphRef.current;
    const now = ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(to, now + FADE);
  };

  const play = useCallback(async () => {
    const { ctx } = ensureGraph();
    try {
      if (audioRef.current.paused) {
        ctx.resume();
        await audioRef.current.play();
      }
      ramp(VOLUME);
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  }, []);

  const stop = useCallback(() => {
    if (!graphRef.current) return;
    ramp(0);
    setPlaying(false);
    const audio = audioRef.current;
    setTimeout(() => { if (graphRef.current.gain.gain.value < 0.01) audio.pause(); }, FADE * 1000 + 100);
  }, []);

  const toggle = useCallback(() => {
    userOffRef.current = playing;
    playing ? stop() : play();
  }, [playing, play, stop]);

  useEffect(() => {
    const events = ["pointerdown", "keydown", "touchend", "wheel", "scroll"];
    const first = (e) => {
      if (userOffRef.current || e.target?.closest?.("[data-sound-toggle]")) return remove();
      play().then(() => { if (audioRef.current && !audioRef.current.paused) remove(); });
    };
    const remove = () => events.forEach((ev) => window.removeEventListener(ev, first));
    events.forEach((ev) => window.addEventListener(ev, first, { passive: true }));
    return () => {
      remove();
      audioRef.current?.pause();
      graphRef.current?.ctx.close();
    };
  }, [play]);

  return { playing, toggle };
};
